import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import {
  NumberField as AriaNumberField,
  type NumberFieldProps as AriaNumberFieldProps,
  useLocale,
  type ValidationResult,
} from 'react-aria-components';
import cx from 'clsx';
import { Description } from '#components/form-elements/description/description';
import { ErrorMessage } from '#components/form-elements/error-message/error-message';
import { Input } from '#components/form-elements/input/input';
import { FieldLabel } from '#components/form-elements/label/label';
import { parseLocalizedNumber } from '#utils/parse-localized-number';
import styles from './amount-field.module.css';

interface AmountFieldComponentProps extends AriaNumberFieldProps {
  amount: string | number | undefined | null;
  label: string;
  isOptional?: boolean;
  description?: string;
  errorMessage?: ReactNode | ((validation: ValidationResult) => ReactNode);
  placeholder?: string;
  currency?: string;
  decimals?: number;
  onAmountChange?: (value: string) => void;
  onFocus?: () => void;
  onFocusChange?: (isFocused: boolean) => void;
  autoFocus?: boolean;
  labelClassName?: string;
  /** Optional node rendered in the input (e.g. an info tooltip icon). */
  amountFieldTooltip?: ReactNode;
}

const getFormattedValue = (
  amount: string | number | undefined | null,
  decimals: number
): number => {
  // . falsy values or zero (type number) set as default are used to show a placeholder
  if (
    amount === null ||
    amount === undefined ||
    amount === '' ||
    amount === 0 ||
    Number.isNaN(amount)
  ) {
    return NaN;
  }

  if (typeof amount === 'number') {
    return parseFloat(amount.toFixed(decimals));
  }

  if (typeof amount === 'string') {
    const parsedAmount = parseLocalizedNumber(amount, decimals);

    //. zero returned from parsed string is ok as it's a user-set value
    if (!isNaN(parsedAmount)) {
      return parseFloat(parsedAmount.toFixed(decimals));
    }
  }

  return NaN;
};

const getDecimalSeparator = (locale: string): string => {
  const part = new Intl.NumberFormat(locale, { minimumFractionDigits: 1 })
    .formatToParts(1.1)
    .find(formatPart => formatPart.type === 'decimal');

  return part?.value ?? '.';
};

const getGroupSeparator = (locale: string): string => {
  const part = new Intl.NumberFormat(locale)
    .formatToParts(1000)
    .find(formatPart => formatPart.type === 'group');

  return part?.value ?? ',';
};

export function AmountField({
  amount,
  label,
  isOptional,
  description,
  errorMessage,
  placeholder = '0.00',
  currency = 'EUR',
  decimals = 2,
  className,
  onAmountChange,
  onFocus,
  onFocusChange,
  autoFocus = false,
  labelClassName,
  amountFieldTooltip,
  minValue,
  maxValue,
  isDisabled,
  ...props
}: AmountFieldComponentProps): ReactElement {
  const [currentValue, setCurrentValue] = useState(() => getFormattedValue(amount, decimals));
  const rawInputRef = useRef<string>('');
  const { locale } = useLocale();

  // . the reconciliation path below (pasted/typed decimals) parses the raw
  // string directly and skips react-aria's own min/max clamping, so bounds
  // must be re-applied here to keep e.g. `minValue={0}` effective on paste.
  // A negative value pasted into a non-negative field (minValue >= 0) is
  // most likely a sign typo rather than an out-of-range magnitude, so it's
  // flipped to its absolute value instead of being floored to `minValue`.
  const clampToBounds = (value: number): number => {
    if (isNaN(value)) {
      return value;
    }

    let result = value;

    if (minValue !== undefined && minValue >= 0 && result < 0) {
      result = Math.abs(result);
    }

    if (minValue !== undefined && result < minValue) {
      result = minValue;
    }

    if (maxValue !== undefined && result > maxValue) {
      result = maxValue;
    }

    return result;
  };

  useEffect(() => {
    const newValue = getFormattedValue(amount, decimals);

    setCurrentValue(newValue);
  }, [amount, decimals]);

  const handleChange = (newValue: number): void => {
    // . when the raw input plausibly contains a decimal separator (i.e. a
    // trailing fractional part no longer than `decimals`), always trust the
    // locale-agnostic parse of it over react-aria's own parsed value.
    // react-aria can silently drop or misinterpret the decimal separator
    // (e.g. numeric-keypad '.' in comma-decimal locales), so we reconcile
    // against parseLocalizedNumber in that case. When the raw input has more
    // fractional digits than `decimals` (e.g. typing "1234.1234" with
    // decimals=2), there's no separator ambiguity to resolve - let react-aria
    // handle its own rounding/truncation instead.
    const rawInput = rawInputRef.current;

    if (rawInput) {
      const lastSeparatorIndex = Math.max(rawInput.lastIndexOf('.'), rawInput.lastIndexOf(','));
      const digitsAfterSeparator =
        lastSeparatorIndex === -1 ? 0 : rawInput.length - lastSeparatorIndex - 1;
      const hasPlausibleDecimal =
        lastSeparatorIndex !== -1 && digitsAfterSeparator > 0 && digitsAfterSeparator <= decimals;

      if (hasPlausibleDecimal) {
        const localizedValue = clampToBounds(parseLocalizedNumber(rawInput, decimals));

        if (!isNaN(localizedValue)) {
          setCurrentValue(localizedValue);
          if (onAmountChange) {
            onAmountChange(localizedValue.toFixed(decimals));
          }
          return;
        }
      }
    }

    if (!isNaN(newValue)) {
      const clampedValue = clampToBounds(newValue);

      setCurrentValue(clampedValue);

      if (onAmountChange) {
        onAmountChange(clampedValue.toFixed(decimals));
      }

      return;
    }

    // . invalid number (including when field is cleared)
    setCurrentValue(NaN);
    if (onAmountChange) {
      onAmountChange('');
    }
  };

  const handleFocus = (): void => {
    if (onFocus) {
      onFocus();
    }
  };

  const handleFocusChange = (isFocused: boolean): void => {
    if (onFocusChange) {
      onFocusChange(isFocused);
    }
  };

  const handleInput = (event: React.FormEvent<HTMLInputElement>): void => {
    const input = event.currentTarget as HTMLInputElement;
    rawInputRef.current = input.value;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    // . react-aria's NumberField validates keystrokes against the *current*
    // locale's decimal/group separators via a native `beforeinput` listener,
    // and silently blocks (preventDefault) any other separator character
    // before it ever reaches the DOM value or our onChange/onInput handlers.
    // This means a numeric-keypad '.' is dropped entirely in comma-decimal
    // locales (e.g. fr-FR) rather than being misparsed - there's nothing to
    // reconcile downstream once that happens. To fix this at the source, we
    // intercept '.'/',' at keydown (which fires before `beforeinput`) and,
    // when the pressed key matches neither the locale's decimal separator
    // nor its group separator (e.g. '.' typed in fr-FR, where the decimal
    // separator is ',' and the group separator is a narrow space), translate
    // it into the locale's decimal separator so react-aria's own validator
    // accepts it naturally. Keys that already match the group separator
    // (e.g. ',' typed in en-US, used as a thousands separator) are left
    // untouched.
    if (event.key !== '.' && event.key !== ',') {
      return;
    }

    const decimalSeparator = getDecimalSeparator(locale);
    const groupSeparator = getGroupSeparator(locale);

    if (event.key === decimalSeparator || event.key === groupSeparator) {
      return;
    }

    event.preventDefault();

    const input = event.currentTarget;

    if (typeof document.execCommand === 'function') {
      document.execCommand('insertText', false, decimalSeparator);
      return;
    }

    const { selectionStart, selectionEnd, value } = input;
    const start = selectionStart ?? value.length;
    const end = selectionEnd ?? value.length;
    const nextValue = `${value.slice(0, start)}${decimalSeparator}${value.slice(end)}`;
    const nativeValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value'
    )?.set;

    nativeValueSetter?.call(input, nextValue);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.setSelectionRange(start + 1, start + 1);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    if (!event.clipboardData) {
      return;
    }
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    rawInputRef.current = pastedData;
    const parsedNumber = parseLocalizedNumber(pastedData, decimals);
    handleChange(parsedNumber);
  };

  const isInvalid = Boolean(errorMessage);

  return (
    <div className={styles.amountFieldContainer}>
      <AriaNumberField
        className={cx(className, styles.amountFieldContent)}
        formatOptions={{
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        }}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        maxValue={maxValue}
        minValue={minValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onFocusChange={handleFocusChange}
        value={currentValue}
        {...props}
      >
        {Boolean(label) && (
          <FieldLabel className={labelClassName} isOptional={isOptional}>
            {label}
          </FieldLabel>
        )}
        <div className={cx(styles['input-wrapper'], 'inputWrapper')}>
          <span
            aria-disabled={isDisabled}
            className={cx(
              styles.currency,
              'body-standard',
              !Number.isNaN(currentValue) && styles['with-value']
            )}
          >
            {currency}
          </span>
          <Input
            autoFocus={autoFocus}
            className={styles.input}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
          />
        </div>
        {Boolean(errorMessage) && <ErrorMessage isInvalid={isInvalid}>{errorMessage}</ErrorMessage>}
        {Boolean(description) && <Description>{description}</Description>}
      </AriaNumberField>
      {Boolean(amountFieldTooltip) && <div className={styles.tooltip}>{amountFieldTooltip}</div>}
    </div>
  );
}
